// File: src/components/admin/AdminProtectedRoute.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Protected Route component for Admin Portal

import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Button } from '@mui/material';
import { useAuth } from '../../app/AuthContext';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const AdminProtectedRoute = ({ children }) => {
  const { user, loading: authLoading, signOut, signInWithGoogle } = useAuth();
  const [adminAccess, setAdminAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        setAdminAccess(false);
        setCheckingAccess(false);
        return;
      }

      try {
        // Try to fetch users list as a way to check admin access
        await adminApi.getUsers();
        setAdminAccess(true);
      } catch (err) {
        const errorInfo = handleAdminApiError(err);
        if (errorInfo.type === 'FORBIDDEN' || errorInfo.type === 'UNAUTHORIZED') {
          setAdminAccess(false);
        } else {
          // For other errors (network, server), we'll assume access is granted
          // and let the individual components handle the error
          setAdminAccess(true);
        }
      } finally {
        setCheckingAccess(false);
      }
    };

    if (!authLoading) {
      checkAdminAccess();
    }
  }, [user, authLoading]);

  if (authLoading || checkingAccess) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user || adminAccess === false) {
    return (
      <Box sx={{ p: 3 }}>
        <h2>Access Denied</h2>
        <p>Please log in with an admin account to access this page.</p>

        {!user ? (
          <Button
            variant="contained"
            color="primary"
            onClick={signInWithGoogle}
            sx={{ mt: 2 }}
          >
            Log In with Google
          </Button>
        ) : (
          <Box>
            <p>Logged in as: {user.email}</p>
            <Button
              variant="contained"
              color="secondary"
              onClick={signOut}
              sx={{ mt: 2 }}
            >
              Log Out
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return children;
};

export default AdminProtectedRoute;

